import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import {
  Search, RefreshCw, TrendingUp, AlertTriangle, CheckCircle,
  Clock, ChevronDown, ChevronUp, Zap, Target, Globe, Tag, FileText,
  Link2, Wand2, X, ArrowRight, Info
} from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const password = localStorage.getItem('idmat_admin');
  return { headers: { 'Authorization': 'Basic ' + btoa('admin:' + password) } };
};

const ScoreCircle = ({ score, size = 'lg' }) => {
  const color = score >= 70 ? '#22c55e' : score >= 40 ? '#F5C500' : '#ef4444';
  const r = size === 'lg' ? 38 : 24;
  const cx = size === 'lg' ? 44 : 30;
  const circumference = 2 * Math.PI * r;
  const strokeDash = (score / 100) * circumference;

  return (
    <div className={`relative inline-flex items-center justify-center ${size === 'lg' ? 'w-24 h-24' : 'w-16 h-16'}`}>
      <svg className="rotate-[-90deg]" width={size === 'lg' ? 88 : 60} height={size === 'lg' ? 88 : 60}>
        <circle cx={cx} cy={cx} r={r} stroke="#e5e7eb" strokeWidth="6" fill="none" />
        <circle
          cx={cx} cy={cx} r={r}
          stroke={color} strokeWidth="6" fill="none"
          strokeDasharray={`${strokeDash} ${circumference}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
      </svg>
      <span className={`absolute font-bold ${size === 'lg' ? 'text-2xl' : 'text-base'}`} style={{ color }}>
        {score}
      </span>
    </div>
  );
};

const NON_AUTO_CATEGORIES = ['technique', 'liens'];

const PreviewModal = ({ preview, onConfirm, onCancel, loading }) => {
  if (!preview) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="preview-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Wand2 size={20} className="text-[#F5C500]" />
            <h3 className="font-bold text-gray-800">Correction générée par l'IA</h3>
          </div>
          <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {preview.explication && (
            <div className="flex items-start gap-2 bg-[#F5C500]/10 border border-[#F5C500]/30 rounded-lg p-3">
              <Info size={14} className="text-[#F5C500] mt-0.5 shrink-0" />
              <p className="text-sm text-gray-700">{preview.explication}</p>
            </div>
          )}

          {preview.preview?.map((item, i) => (
            <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-200">
                <div className="p-4">
                  <p className="text-xs text-red-500 font-semibold mb-1">AVANT</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.avant || <span className="italic text-gray-400">Vide</span>}</p>
                  {item.avant && <p className="text-xs text-gray-400 mt-1">{item.avant.length} caractères</p>}
                </div>
                <div className="p-4 bg-green-50/50">
                  <p className="text-xs text-green-600 font-semibold mb-1">APRÈS</p>
                  <p className="text-sm text-gray-800 leading-relaxed font-medium">{item.apres}</p>
                  <p className="text-xs text-gray-400 mt-1">{item.apres?.length} caractères</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-100">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-apply-btn"
            className="flex-1 py-2.5 bg-[#F5C500] text-[#1a1a1a] rounded-lg font-bold text-sm hover:bg-[#e6b800] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            {loading ? 'Application...' : 'Confirmer & Appliquer sur le site'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ManualModal = ({ suggestion, onClose }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Info size={20} className="text-blue-500" />
          <h3 className="font-bold text-gray-800">Correction manuelle requise</h3>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-400" /></button>
      </div>
      <div className="p-5">
        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <p className="text-sm font-semibold text-blue-800 mb-1">Action à réaliser</p>
          <p className="text-sm text-blue-700">{suggestion?.action}</p>
        </div>
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">Comment faire :</p>
          {suggestion?.categorie === 'technique' && (
            <ol className="space-y-2 text-sm text-gray-600">
              <li className="flex gap-2"><span className="text-[#F5C500] font-bold">1.</span>Cette correction nécessite une modification technique du code.</li>
              <li className="flex gap-2"><span className="text-[#F5C500] font-bold">2.</span>Contactez votre développeur ou revenez dans ce chat pour que je l'applique.</li>
            </ol>
          )}
          {suggestion?.categorie === 'liens' && (
            <ol className="space-y-2 text-sm text-gray-600">
              <li className="flex gap-2"><span className="text-[#F5C500] font-bold">1.</span>Allez dans <strong>Admin → Contenu (CMS)</strong></li>
              <li className="flex gap-2"><span className="text-[#F5C500] font-bold">2.</span>Modifiez les textes des boutons et liens de navigation</li>
              <li className="flex gap-2"><span className="text-[#F5C500] font-bold">3.</span>Ajoutez des liens internes entre vos pages</li>
            </ol>
          )}
        </div>
      </div>
      <div className="p-5 border-t border-gray-100">
        <button onClick={onClose} className="w-full py-2.5 bg-[#1a1a1a] text-white rounded-lg font-medium hover:bg-gray-800 transition-colors text-sm">
          Compris
        </button>
      </div>
    </div>
  </div>
);


const CATEGORIE_CONFIG = {
  titre: { label: 'Titre', icon: Tag, color: 'bg-purple-100 text-purple-700' },
  description: { label: 'Description', icon: FileText, color: 'bg-blue-100 text-blue-700' },
  contenu: { label: 'Contenu', icon: Globe, color: 'bg-green-100 text-green-700' },
  seo_local: { label: 'SEO Local', icon: Target, color: 'bg-yellow-100 text-yellow-700' },
  technique: { label: 'Technique', icon: Zap, color: 'bg-red-100 text-red-700' },
  liens: { label: 'Liens', icon: Link2, color: 'bg-indigo-100 text-indigo-700' },
};

const PRIORITE_CONFIG = {
  haute: { label: 'Haute', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  moyenne: { label: 'Moyenne', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
  faible: { label: 'Faible', color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
};

const STATUT_CONFIG = {
  en_attente: { label: 'En attente', icon: Clock, color: 'text-gray-500' },
  appliquee: { label: 'Appliquée', icon: CheckCircle, color: 'text-green-600' },
  ignoree: { label: 'Ignorée', icon: AlertTriangle, color: 'text-gray-400' },
};

const SuggestionCard = ({ suggestion, analysisId, pageUrl, onUpdate, onAutoFix }) => {
  const [loading, setLoading] = useState(false);
  const cat = CATEGORIE_CONFIG[suggestion.categorie] || CATEGORIE_CONFIG.contenu;
  const prio = PRIORITE_CONFIG[suggestion.priorite] || PRIORITE_CONFIG.faible;
  const CatIcon = cat.icon;
  const isAutoApplicable = !NON_AUTO_CATEGORIES.includes(suggestion.categorie);

  const updateStatus = async (status) => {
    setLoading(true);
    try {
      await axios.put(`${API}/api/admin/seo/suggestion`,
        { analysis_id: analysisId, page_url: pageUrl, suggestion_id: suggestion.id, status },
        getAuthHeader()
      );
      onUpdate(suggestion.id, status);
      toast.success('Statut mis à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const isApplied = suggestion.statut === 'appliquee';
  const isIgnored = suggestion.statut === 'ignoree';

  return (
    <div data-testid={`suggestion-${suggestion.id}`}
      className={`border rounded-lg p-4 transition-all ${isApplied ? 'bg-green-50 border-green-200 opacity-75' : isIgnored ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-white border-gray-200 hover:border-[#F5C500] hover:shadow-sm'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cat.color}`}>
              <CatIcon size={11} />
              {cat.label}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${prio.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${prio.dot}`}></span>
              Priorité {prio.label}
            </span>
            {isApplied && <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle size={12} /> Appliquée</span>}
            {isAutoApplicable && !isApplied && !isIgnored && (
              <span className="inline-flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded font-medium">
                <Wand2 size={10} /> Auto-corrigeable
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-800 mb-1">{suggestion.action}</p>
          <p className="text-xs text-gray-500 mb-2">{suggestion.probleme}</p>
          <div className="flex items-start gap-1 bg-[#F5C500]/10 rounded p-2">
            <TrendingUp size={12} className="text-[#F5C500] mt-0.5 shrink-0" />
            <p className="text-xs text-gray-600">{suggestion.impact}</p>
          </div>
        </div>

        {!isIgnored && !isApplied && (
          <div className="flex flex-col gap-1 shrink-0 min-w-[110px]">
            {isAutoApplicable ? (
              <button
                onClick={() => onAutoFix(suggestion, pageUrl)}
                disabled={loading}
                data-testid={`auto-fix-${suggestion.id}`}
                className="text-xs px-3 py-1.5 bg-[#1a1a1a] text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-1 justify-center font-medium"
              >
                <Wand2 size={11} /> Corriger auto
              </button>
            ) : (
              <button
                onClick={() => onAutoFix(suggestion, pageUrl)}
                disabled={loading}
                className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 justify-center font-medium"
              >
                <Info size={11} /> Comment faire
              </button>
            )}
            <button
              onClick={() => updateStatus('ignoree')}
              disabled={loading}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 text-center"
            >
              Ignorer
            </button>
          </div>
        )}
        {(isApplied || isIgnored) && (
          <button
            onClick={() => updateStatus('en_attente')}
            disabled={loading}
            className="text-xs px-2 py-1 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          >
            Réinitialiser
          </button>
        )}
      </div>
    </div>
  );
};

const PageAnalysis = ({ page, analysisId, onSuggestionUpdate, onAutoFix }) => {
  const [expanded, setExpanded] = useState(false);
  const pending = page.suggestions?.filter(s => s.statut === 'en_attente').length || 0;
  const applied = page.suggestions?.filter(s => s.statut === 'appliquee').length || 0;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <ScoreCircle score={page.score} size="sm" />
          <div className="text-left">
            <p className="font-semibold text-gray-800">{page.page}</p>
            <p className="text-xs text-gray-500">{page.url}</p>
            <div className="flex gap-3 mt-1">
              {pending > 0 && <span className="text-xs text-orange-600">{pending} suggestion{pending > 1 ? 's' : ''}</span>}
              {applied > 0 && <span className="text-xs text-green-600">{applied} appliquée{applied > 1 ? 's' : ''}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {page.points_forts?.length > 0 && (
            <div className="hidden sm:flex gap-1">
              {page.points_forts.slice(0, 2).map((pf, i) => (
                <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{pf}</span>
              ))}
            </div>
          )}
          {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          {page.points_forts?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Points forts</p>
              <div className="flex flex-wrap gap-2">
                {page.points_forts.map((pf, i) => (
                  <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle size={10} /> {pf}
                  </span>
                ))}
              </div>
            </div>
          )}

          {page.suggestions?.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Suggestions ({page.suggestions.length})
              </p>
              <div className="space-y-3">
                {page.suggestions
                  .sort((a, b) => {
                    const order = { haute: 0, moyenne: 1, faible: 2 };
                    return (order[a.priorite] ?? 3) - (order[b.priorite] ?? 3);
                  })
                  .map(sug => (
                    <SuggestionCard
                      key={sug.id}
                      suggestion={sug}
                      analysisId={analysisId}
                      pageUrl={page.url}
                      onUpdate={(id, status) => onSuggestionUpdate(page.url, id, status)}
                      onAutoFix={onAutoFix}
                    />
                  ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">Aucune suggestion pour cette page.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function AdminSEO() {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [activeTab, setActiveTab] = useState('analyse');

  // Auto-fix modal state
  const [fixModal, setFixModal] = useState(null); // { preview, suggestion, pageUrl } | null
  const [fixLoading, setFixLoading] = useState(false);
  const [manualModal, setManualModal] = useState(null); // suggestion | null

  useEffect(() => {
    const pwd = localStorage.getItem('idmat_admin');
    if (!pwd) { navigate('/admin'); return; }
    fetchLatest();
    fetchHistory();
  }, []);

  const fetchLatest = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/seo/analyses/latest`, getAuthHeader());
      if (res.data) {
        setAnalysis(res.data);
        setSelectedAnalysisId(res.data.id);
      }
    } catch (err) {
      console.error('Error fetching latest analysis:', err);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/api/admin/seo/analyses`, getAuthHeader());
      setHistory(res.data || []);
    } catch {
      /* silent */
    } finally {
      setLoadingHistory(false);
    }
  };

  const runAnalysis = async () => {
    setLoading(true);
    toast.info('Analyse SEO lancée... (30-60 secondes)', { duration: 90000, id: 'seo-progress' });
    try {
      // Start background job
      const startRes = await axios.post(`${API}/api/admin/seo/analyze`, {}, getAuthHeader());
      const jobId = startRes.data.job_id;

      // Poll every 5 seconds
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await axios.get(`${API}/api/admin/seo/job/${jobId}`, getAuthHeader());
          const { status, result, error } = pollRes.data;
          if (status === 'done' && result) {
            clearInterval(pollInterval);
            setAnalysis(result);
            setSelectedAnalysisId(result.id);
            fetchHistory();
            setLoading(false);
            toast.dismiss('seo-progress');
            toast.success('Analyse SEO terminée !');
          } else if (status === 'error') {
            clearInterval(pollInterval);
            setLoading(false);
            toast.dismiss('seo-progress');
            toast.error(`Erreur : ${error || 'Analyse échouée'}`);
          }
        } catch {
          // Keep polling on transient errors
        }
      }, 5000);

      // Safety timeout after 3 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (loading) {
          setLoading(false);
          toast.dismiss('seo-progress');
          toast.error('Délai dépassé. Réessayez dans quelques instants.');
        }
      }, 180000);

    } catch (err) {
      setLoading(false);
      toast.dismiss('seo-progress');
      toast.error('Erreur lors du lancement de l\'analyse.');
    }
  };

  const handleSuggestionUpdate = (pageUrl, suggestionId, newStatus) => {
    setAnalysis(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        pages: prev.pages.map(p =>
          p.url === pageUrl
            ? { ...p, suggestions: p.suggestions.map(s => s.id === suggestionId ? { ...s, statut: newStatus } : s) }
            : p
        )
      };
    });
  };

  const handleAutoFix = async (suggestion, pageUrl) => {
    if (NON_AUTO_CATEGORIES.includes(suggestion.categorie)) {
      setManualModal(suggestion);
      return;
    }
    setFixLoading(true);
    toast.info("L'IA génère la correction...", { duration: 20000, id: 'fix-progress' });
    try {
      const res = await axios.post(`${API}/api/admin/seo/preview-fix`, {
        analysis_id: analysis.id,
        page_url: pageUrl,
        suggestion_id: suggestion.id,
      }, getAuthHeader());

      toast.dismiss('fix-progress');
      if (!res.data.auto_applicable) {
        setManualModal(suggestion);
      } else {
        setFixModal({ preview: res.data, suggestion, pageUrl });
      }
    } catch {
      toast.dismiss('fix-progress');
      toast.error('Erreur lors de la génération de la correction.');
    } finally {
      setFixLoading(false);
    }
  };

  const confirmAutoFix = async () => {
    if (!fixModal) return;
    setFixLoading(true);
    try {
      const corrections = {};
      fixModal.preview.preview.forEach(item => { corrections[item.field] = item.apres; });
      await axios.post(`${API}/api/admin/seo/apply-fix`, {
        analysis_id: fixModal.preview.analysis_id,
        page_url: fixModal.preview.page_url,
        suggestion_id: fixModal.preview.suggestion_id,
        corrections,
      }, getAuthHeader());
      handleSuggestionUpdate(fixModal.pageUrl, fixModal.suggestion.id, 'appliquee');
      setFixModal(null);
      toast.success('Correction appliquée sur le site !');
    } catch {
      toast.error('Erreur lors de l\'application.');
    } finally {
      setFixLoading(false);
    }
  };

  const loadHistoryItem = async (item) => {
    setAnalysis(item);
    setSelectedAnalysisId(item.id);
    setActiveTab('analyse');
  };

  const totalSuggestions = analysis?.pages?.reduce((acc, p) => acc + (p.suggestions?.length || 0), 0) || 0;
  const pendingSuggestions = analysis?.pages?.reduce((acc, p) => acc + (p.suggestions?.filter(s => s.statut === 'en_attente').length || 0), 0) || 0;
  const appliedSuggestions = analysis?.pages?.reduce((acc, p) => acc + (p.suggestions?.filter(s => s.statut === 'appliquee').length || 0), 0) || 0;
  const highPriority = analysis?.pages?.reduce((acc, p) => acc + (p.suggestions?.filter(s => s.priorite === 'haute' && s.statut === 'en_attente').length || 0), 0) || 0;

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />

      {/* Auto-fix preview modal */}
      {fixModal && (
        <PreviewModal
          preview={fixModal.preview}
          onConfirm={confirmAutoFix}
          onCancel={() => setFixModal(null)}
          loading={fixLoading}
        />
      )}

      {/* Manual instruction modal */}
      {manualModal && (
        <ManualModal suggestion={manualModal} onClose={() => setManualModal(null)} />
      )}
      <div className="max-w-5xl mx-auto" data-testid="admin-seo-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Assistant SEO IA
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Analyse automatique et suggestions pour améliorer votre classement Google
            </p>
          </div>
          <button
            onClick={runAnalysis}
            disabled={loading}
            data-testid="run-seo-analysis-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#F5C500] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#e6b800] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <><RefreshCw size={16} className="animate-spin" /> Analyse en cours...</>
            ) : (
              <><Search size={16} /> Lancer une analyse</>
            )}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
          {['analyse', 'historique', 'mots_cles'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${activeTab === tab ? 'bg-white text-[#1a1a1a] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'analyse' ? 'Analyse' : tab === 'historique' ? 'Historique' : 'Mots-clés'}
            </button>
          ))}
        </div>

        {/* No analysis yet */}
        {!analysis && activeTab === 'analyse' && (
          <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-200">
            <Search size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune analyse disponible</h3>
            <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
              Lancez votre première analyse SEO pour obtenir des suggestions personnalisées pour votre site.
            </p>
            <button
              onClick={runAnalysis}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#F5C500] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#e6b800] transition-colors"
            >
              <Search size={16} /> Lancer la première analyse
            </button>
          </div>
        )}

        {/* Analysis Tab */}
        {analysis && activeTab === 'analyse' && (
          <div className="space-y-6">
            {/* Score + Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="text-center">
                  <ScoreCircle score={analysis.score_global} size="lg" />
                  <p className="text-xs text-gray-500 mt-2 font-medium">Score global</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp size={16} className="text-[#F5C500]" />
                    <h3 className="font-semibold text-gray-800">Résumé de l'analyse</h3>
                    <span className="text-xs text-gray-400 ml-auto">
                      {new Date(analysis.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{analysis.resume}</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <p className="text-xl font-bold text-orange-600">{pendingSuggestions}</p>
                      <p className="text-xs text-gray-500">En attente</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-xl font-bold text-red-600">{highPriority}</p>
                      <p className="text-xs text-gray-500">Priorité haute</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-xl font-bold text-green-600">{appliedSuggestions}</p>
                      <p className="text-xs text-gray-500">Appliquées</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Priority Actions */}
            {analysis.actions_prioritaires?.length > 0 && (
              <div className="bg-[#1a1a1a] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Zap size={16} className="text-[#F5C500]" />
                  <h3 className="font-semibold text-white text-sm">Actions prioritaires</h3>
                </div>
                <ol className="space-y-2">
                  {analysis.actions_prioritaires.map((action, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <span className="text-[#F5C500] font-bold shrink-0">{i + 1}.</span>
                      {action}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Pages */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Analyse par page ({analysis.pages?.length || 0} pages)
              </h3>
              <div className="space-y-3">
                {analysis.pages?.map(page => (
                  <PageAnalysis
                    key={page.url}
                    page={page}
                    analysisId={analysis.id}
                    onSuggestionUpdate={handleSuggestionUpdate}
                    onAutoFix={handleAutoFix}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'historique' && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Historique des analyses</h3>
              <p className="text-xs text-gray-500">Les 20 dernières analyses SEO</p>
            </div>
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <RefreshCw size={20} className="animate-spin text-gray-400" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-8">Aucun historique disponible</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {history.map(item => (
                  <button
                    key={item.id}
                    onClick={() => loadHistoryItem(item)}
                    className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left ${selectedAnalysisId === item.id ? 'bg-yellow-50 border-l-2 border-l-[#F5C500]' : ''}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {new Date(item.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.resume}</p>
                    </div>
                    <div className="shrink-0 ml-4">
                      <ScoreCircle score={item.score_global} size="sm" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Keywords Tab */}
        {activeTab === 'mots_cles' && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Mots-clés recommandés</h3>
              <p className="text-xs text-gray-500">Mots-clés longue traîne suggérés par l'IA pour La Réunion</p>
            </div>
            {!analysis?.mots_cles_recommandes?.length ? (
              <p className="text-center text-gray-500 text-sm py-8">Lancez une analyse pour obtenir des recommandations de mots-clés</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-semibold text-gray-600">Mot-clé</th>
                      <th className="text-center p-3 font-semibold text-gray-600">Volume</th>
                      <th className="text-center p-3 font-semibold text-gray-600">Difficulté</th>
                      <th className="text-left p-3 font-semibold text-gray-600">Page cible</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {analysis.mots_cles_recommandes.map((kw, i) => {
                      const volColor = kw.volume_estime === 'élevé' ? 'text-green-600 bg-green-50' : kw.volume_estime === 'moyen' ? 'text-yellow-600 bg-yellow-50' : 'text-gray-500 bg-gray-100';
                      const diffColor = kw.difficulte === 'faible' ? 'text-green-600 bg-green-50' : kw.difficulte === 'moyenne' ? 'text-yellow-600 bg-yellow-50' : 'text-red-600 bg-red-50';
                      return (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="p-3 font-medium text-gray-800">{kw.mot_cle}</td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${volColor}`}>{kw.volume_estime}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${diffColor}`}>{kw.difficulte}</span>
                          </td>
                          <td className="p-3 text-gray-500 text-xs font-mono">{kw.page_cible}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
